import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Plus, ArrowRightLeft } from "lucide-react";

/** Editor manuale dei gironi: sposta, rimuovi, reinserisci. Il ranking non vincola nulla. */
export function PoolsEditor({ pools, participants, nameOf, rankOf, onSave, onCancel }) {
  const [draft, setDraft] = useState(() => pools.map((p) => [...p.participant_keys]));
  useEffect(() => { setDraft(pools.map((p) => [...p.participant_keys])); }, [pools]);
  const inPools = new Set(draft.flat());
  const bench = participants.filter((p) => !inPools.has(p.key));
  const move = (key, from, to) => setDraft((d) => d.map((g, i) => (i === from ? g.filter((k) => k !== key) : i === to ? [...g, key] : g)));
  const remove = (key, from) => setDraft((d) => d.map((g, i) => (i === from ? g.filter((k) => k !== key) : g)));
  const add = (key, to) => setDraft((d) => d.map((g, i) => (i === to ? [...g, key] : g)));
  const addPool = () => setDraft((d) => [...d, []]);
  const removePool = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const invalid = draft.some((g) => g.length < 2) || draft.length === 0;

  return (
    <div className="bg-white border border-zinc-300 rounded-md p-4 space-y-4" data-testid="pools-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading font-semibold text-sm flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" />Modifica gironi</div>
        <div className="text-xs text-zinc-500">Gli assalti già disputati tra coppie che restano nello stesso girone vengono conservati.</div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {draft.map((g, gi) => (
          <div key={gi} className="border border-zinc-200 rounded p-3" data-testid={`pool-editor-${gi + 1}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-zinc-500">Girone {gi + 1} · {g.length}</div>
              <button data-testid={`pool-editor-remove-pool-${gi + 1}`} onClick={() => removePool(gi)} className="text-xs text-zinc-400 hover:text-red-600">rimuovi girone</button>
            </div>
            {g.length < 2 && <div className="text-xs text-amber-700 mb-1">Servono almeno 2 atleti.</div>}
            <ul className="space-y-1">
              {g.map((k) => (
                <li key={k} className="flex items-center gap-2 text-sm" data-testid={`pool-editor-member-${k}`}>
                  <span className="font-mono text-[10px] text-zinc-400 w-8">{rankOf(k) === 9999 ? "—" : `R${rankOf(k)}`}</span>
                  <span className="flex-1 truncate">{nameOf(k)}</span>
                  <select data-testid={`pool-editor-move-${k}`} value={gi} onChange={(e) => move(k, gi, parseInt(e.target.value))} className="text-xs border border-zinc-300 rounded px-1 py-0.5 bg-white">
                    {draft.map((_, j) => <option key={j} value={j}>G{j + 1}</option>)}
                  </select>
                  <button data-testid={`pool-editor-remove-${k}`} onClick={() => remove(k, gi)} className="p-1 text-zinc-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <button data-testid="pool-editor-add-pool" onClick={addPool} className="border border-dashed border-zinc-300 rounded p-3 text-sm text-zinc-500 hover:border-black hover:text-black flex items-center justify-center gap-1"><Plus className="w-4 h-4" />Nuovo girone</button>
      </div>
      {bench.length > 0 && (
        <div className="border-t border-zinc-100 pt-3" data-testid="pool-editor-bench">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Fuori dai gironi · {bench.length}</div>
          <ul className="flex flex-wrap gap-2">
            {bench.map((p) => (
              <li key={p.key} className="flex items-center gap-1 text-sm border border-zinc-200 rounded-full pl-3 pr-1 py-0.5" data-testid={`pool-editor-bench-${p.key}`}>
                {nameOf(p.key)}
                <select data-testid={`pool-editor-add-${p.key}`} value="" onChange={(e) => e.target.value !== "" && add(p.key, parseInt(e.target.value))} className="text-xs border-0 bg-transparent">
                  <option value="">↳ in…</option>
                  {draft.map((_, j) => <option key={j} value={j}>G{j + 1}</option>)}
                </select>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" data-testid="pool-editor-cancel" onClick={onCancel}>Annulla</Button>
        <Button data-testid="pool-editor-save" disabled={invalid} onClick={() => onSave(draft)} className="bg-black hover:bg-zinc-800">Salva gironi</Button>
      </div>
    </div>
  );
}
